/**
 * Friendly, user-facing error messages.
 *
 * Resolution order (see `shared/utils/apiErrorMessage.ts`):
 *   1. errors.codes.<E_CODE>   — backend domain error code (most specific)
 *   2. errors.validation       — VineJS 422 (field validation)
 *   3. errors.status.<http>    — HTTP status fallback
 *   4. errors.network          — fetch/connection failure
 *   5. errors.generic          — last resort
 *
 * Unmapped backend codes fall back to the HTTP-status message, so it's always
 * friendly even without a dedicated entry. To add a new message, drop the
 * backend `code` (the `E_...` string) as a key under `codes` here and in `pt`.
 */
const errors = {
  title: "Error",
  generic: "Something went wrong. Please try again.",
  network: "Connection failed. Check your internet and try again.",
  timeout: "The request took too long. Please try again.",
  validation: "Please review the highlighted fields and try again.",
  rateLimited: "Too many attempts. Please wait a moment and try again.",

  status: {
    "400": "We couldn't process this request. Please check the data and try again.",
    "401": "Your session has expired. Please sign in again.",
    "403": "You don't have permission to perform this action.",
    "404": "We couldn't find what you were looking for.",
    "409": "This action conflicts with the current state. Please refresh and try again.",
    "413": "The file is too large.",
    "422": "Please review the highlighted fields and try again.",
    "429": "Too many attempts. Please wait a moment and try again.",
    "500": "Something went wrong on our side. Please try again shortly.",
    "502": "The service is temporarily unavailable. Please try again shortly.",
    "503": "The service is temporarily unavailable. Please try again shortly.",
  },

  codes: {
    // Auth
    E_INVALID_CREDENTIALS: "Invalid email or password.",
    E_INVALID_TOKEN: "Your session has expired. Please sign in again.",
    E_USER_PROFILE_MISSING: "We couldn't find your profile. Please contact support.",
    E_OAUTH_EXCHANGE_FAILED: "We couldn't complete the sign-in. Please try again.",
    E_INSUFFICIENT_ROLE: "You don't have permission to perform this action.",
    E_INSUFFICIENT_SCOPE: "You don't have access to this resource.",
    E_INVALID_PASSWORD_RESET_TOKEN: "This password reset link is invalid or has expired.",
    E_EMAIL_SEND_FAILED: "We couldn't send the email. Please try again shortly.",

    // Invites & registration
    E_INVITE_NOT_FOUND: "This invitation no longer exists.",
    E_INVITE_TOKEN_REQUIRED: "The invitation link is incomplete.",
    E_INVITE_TOKEN_INVALID: "This invitation link is invalid or has expired.",
    E_REGISTRATION_FAILED: "We couldn't complete your registration. Please try again.",
    E_PASSWORD_TOO_LONG: "The password is too long.",
    E_USER_ALREADY_EXISTS: "An account with this email already exists.",
    E_USER_IN_ANOTHER_ORG: "This user already belongs to another organization.",
    E_INVITE_ALREADY_EXISTS: "An invitation has already been sent to this email.",
    E_SUBSCRIPTION_REQUIRED: "An active subscription is required for this action.",
    E_USER_LIMIT_REACHED: "You've reached the user limit for your plan.",

    // Users
    E_INVALID_DELETION_PAYLOAD: "Please confirm your password to delete the account.",
    E_USER_NOT_FOUND: "We couldn't find this user.",
    E_EMAIL_IN_USE: "This email is already in use.",
    E_EMPTY_UPDATE: "There are no changes to save.",
    E_CANNOT_ASSIGN_SUPERADMIN: "You can't assign the super admin role.",
    E_CANNOT_MODIFY_SUPERADMIN: "You can't modify a super admin.",
    E_AVATAR_INVALID: "This image is not valid. Use a PNG or JPG file.",
    E_AVATAR_PERMISSION_DENIED: "You don't have permission to change this avatar.",
    E_INVALID_USER_SETTINGS_PAYLOAD: "Some settings are invalid. Please review and try again.",

    // Products & organizations
    E_PRODUCT_ACCESS_DENIED: "You don't have access to this workspace.",
    E_PRODUCT_NOT_FOUND: "We couldn't find this workspace.",
    E_USER_ACCESS_DENIED: "You don't have access to this resource.",
    E_USER_ORGANIZATION_MISSING: "Your account isn't linked to an organization.",
    E_ORGANIZATION_REQUIRED: "Please select an organization to continue.",
    E_ORGANIZATION_NOT_FOUND: "We couldn't find this organization.",
    E_ORGANIZATION_ACCESS_DENIED: "You don't have access to this organization.",
    E_ORGANIZATION_UPDATE_FORBIDDEN: "You don't have permission to update this organization.",
    E_ORGANIZATION_DELETE_FORBIDDEN: "You don't have permission to delete this organization.",
    E_ORGANIZATION_HAS_PRODUCTS: "Remove the workspaces before deleting this organization.",
    E_ORGANIZATION_HAS_DEPENDENCIES: "This organization still has linked data and can't be deleted.",
    E_EMBEDDING_MODEL_INCOMPATIBLE:
      "This embedding model produces vectors of a size the Brain does not store.",
    E_LAST_ADMIN_REMOVAL_FORBIDDEN: "You can't remove the last admin of this workspace.",
    E_PRODUCT_DELETION_BLOCKED: "This workspace can't be deleted while it has linked data.",
    E_PRODUCT_MEMBERSHIP_CONFLICT: "This user is already a member of the workspace.",
    E_INVALID_ORGANIZATION_OVERRIDE: "The selected organization is invalid.",
    E_LOGO_INVALID: "This logo image is not valid. Use a PNG or JPG file.",
    E_LOGO_UPLOAD_FAILED: "We couldn't upload the logo. Please try again.",

    // Integrations
    E_INTEGRATION_NOT_FOUND: "We couldn't find this tool.",
    E_INTEGRATION_TEMPLATE_NOT_FOUND: "We couldn't find this tool template.",
    E_INTEGRATION_TEMPLATE_HAS_INSTANCES: "Remove the installed tools before deleting this template.",
    E_INTEGRATION_BASE_NOT_FOUND: "We couldn't find this tool.",
    E_INTEGRATION_BASE_ACCESS_DENIED: "You don't have access to this tool.",
    E_INTEGRATION_BASE_ALREADY_EXISTS: "This tool is already installed.",
    E_INTEGRATION_BASE_IN_USE:
      "This tool is in use as an AI provider. Pick another provider before deleting it.",
    E_INTEGRATION_TITLE_TAKEN: "There is already a tool with this name in the organization. Choose another one.",
    E_MCP_SERVER_URL_REJECTED: "This server URL is not allowed. Use a public HTTPS address.",
    E_MCP_TOOLS_CHANGED: "The MCP server changed its tools since the preview. Review the new changes before applying.",
    E_MCP_TOOL_LIST_TOO_LARGE: "This MCP server offers too many tools (more than 500 or over 1 MB of definitions) for Prodgy to store.",
    E_MCP_SERVER_UNREACHABLE: "We couldn't reach the MCP server. Check the URL and the credentials and try again.",
    E_MCP_OAUTH_FAILED: "The authorization with the provider failed. Try reconnecting.",
    E_INTEGRATION_KIND_MISMATCH: "This action is not available for this type of tool.",
    E_INTEGRATION_CREDENTIALS_REJECTED:
      "The provider rejected the credentials. Check the key and the remaining fields before saving.",
    E_PLATFORM_ORGANIZATION_MISSING:
      "The platform AI settings are not available in this environment yet.",
    E_PLATFORM_INTEGRATION_UNAVAILABLE:
      "This tool does not belong to the platform. Register it under the platform tools.",
    E_PLATFORM_MODEL_NOT_CONFIGURED:
      "No platform AI model is configured for this purpose. Set it up in the Platform area.",
    E_PLATFORM_MODEL_UNRESOLVED:
      "The AI provider chosen for this purpose could not be used. Review the setup in the Platform area.",
    E_INVALID_INTEGRATION_CREDENTIALS: "The credentials are invalid. Please check and try again.",
    E_UNSUPPORTED_AUTH_TYPE: "This authentication type isn't supported.",
    E_DECRYPTION_FAILED: "We couldn't read the stored credentials. Please re-enter them.",

    // Knowledge base
    E_KNOWLEDGE_BASE_NOT_FOUND: "We couldn't find this Brain.",
    E_KNOWLEDGE_BASE_ACCESS_DENIED: "You don't have access to this Brain.",
    E_KNOWLEDGE_BASE_HAS_CONTENT: "Remove the documents before deleting this Brain.",
    E_EMBEDDING_PROVIDER_UNAVAILABLE: "The embedding service is unavailable right now. Please try again shortly.",
    E_UNSUPPORTED_FILE_TYPE: "This file type isn't supported.",
    E_CONTENT_QUALITY_REJECTED: "This content couldn't be processed. Try a different file.",
    E_EMBEDDING_NOT_FOUND: "We couldn't find this embedding.",
    E_VECTOR_SEARCH_FAILED: "The search failed. Please try again.",

    // Storage
    E_STORAGE_UPLOAD_FAILED: "We couldn't upload the file. Please try again.",
    E_STORAGE_REMOVE_FAILED: "We couldn't delete the file. Please try again.",
    E_STORAGE_ITEM_NOT_FOUND: "We couldn't find this file.",
    E_STORAGE_INVALID_FILE: "This file is not valid.",
    E_STORAGE_INGESTION_FAILED: "We couldn't process this file. Please try again.",

    // Workflows
    E_AGENT_TEMPLATE_NOT_FOUND: "We couldn't find this workflow template.",
    E_AGENT_BASE_NOT_FOUND: "We couldn't find this workflow.",
    E_AGENT_BASE_ACCESS_DENIED: "You don't have access to this workflow.",
    E_AGENT_ALREADY_INSTALLED: "This workflow is already installed.",
    E_AGENT_VERSION_CONFLICT: "This workflow was updated elsewhere. Please refresh and try again.",

    // Chat & playground
    E_CHAT_NOT_FOUND: "We couldn't find this conversation.",
    E_CHAT_ACCESS_DENIED: "You don't have access to this conversation.",
    E_CHAT_STREAM_UNAVAILABLE: "The chat service is unavailable right now. Please try again shortly.",
    E_AGENTIC_SERVICE_UNAVAILABLE: "The assistant is unavailable right now. Please try again shortly.",
    E_PLAYGROUND_CHAT_PAYMENT_REQUIRED: "An active subscription is required to use the playground.",

    // Subscriptions & invoices
    E_SUBSCRIPTION_NOT_FOUND: "We couldn't find a subscription.",
    E_SUBSCRIPTION_NOT_ACTIVE: "Your subscription isn't active.",
    E_NO_SUBSCRIPTION_FOR_ORG: "This organization doesn't have an active subscription.",
    E_INVOICE_NOT_FOUND: "We couldn't find this invoice.",
    E_INVOICE_ALREADY_PAID: "This invoice has already been paid.",
    E_PAYMENT_FAILED: "The payment couldn't be processed. Please try again.",

    // Workflows
    E_WORKFLOW_NOT_FOUND: "We couldn't find this workflow.",
    E_WORKFLOW_BASE_NOT_FOUND: "We couldn't find this workflow.",
    E_NODE_NOT_FOUND: "We couldn't find this node.",
    E_TRIGGER_NOT_FOUND: "We couldn't find this trigger.",

    // Settings
    E_SETTING_NOT_FOUND: "We couldn't find this setting.",
    E_CONTACT_ALREADY_EXISTS: "This contact already exists.",

    // Facets
    E_FACET_NOT_FOUND: "We couldn't find this facet.",
    E_FACET_CONFLICT: "A facet with this name and value already exists in this organization.",
    E_FACET_VALIDATION_FAILED: "Some facet fields are invalid. Please review and try again.",
    E_FACET_PARENT_INVALID: "The selected parent facet is invalid.",
    E_FACET_CYCLE_DETECTED: "This would create a cycle in the facet hierarchy.",
    E_FACET_IN_USE: "This facet is in use and can't be deleted.",
    E_FACET_DEPTH_EXCEEDED: "The facet hierarchy is too deep.",

    // Communicator
    E_COMMUNICATOR_INTEGRATION_NOT_FOUND: "We couldn't find this communication channel.",
    E_COMMUNICATOR_LINK_INVALID: "This link is invalid.",
    E_COMMUNICATOR_LINK_EXPIRED: "This link has expired.",

    // Email service
    E_EMAIL_SERVICE_CONNECTION_FAILED: "We couldn't connect to the email provider. Please check the settings.",
    E_EMAIL_SERVICE_SEND_FAILED: "We couldn't send the email. Please try again shortly.",

    // Upstream / proxy
    E_UPSTREAM_UNAVAILABLE: "The service is temporarily unavailable. Please try again shortly.",

    // Client-side guards (frontend: malformed response, broken stream)
    E_UNEXPECTED_RESPONSE: "Unexpected response from the server. Please try again.",
    E_INSTALL_STREAM_FAILED: "We couldn't complete the installation. Please try again.",
  },
} as const;

export default errors;
